import { useTranslation } from 'next-i18next'
import { Button, Tabs } from '@gate/gui'
import { useStore } from '@/store/index'
import React, { useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Flex } from '@mantine/core'
import { useDebounceFn } from 'ahooks'
import { useRouter } from 'next/router'
import useRateChange from '@/hooks/useRateChange'
import BaseDrawer from '@/components/BaseDrawer'
import { HOME_PAGE_URL } from '@/constants/index'
import { bigNumberDividedBy, bigNumberMultipliedBy } from '@/utils/tools'
import FFRounds from './FFRounds'
import FFTags from './FFTags'
import FFInvestors from './FFInvestors'
import FFAmount from './FFAmount'
import { FundingFilterStyled, FundingFilterFooterStyled } from './style'

export enum FundingFilterTabType {
  /** 详细轮次 */
  Rounds = 'p.funding',
  /** 标签 */
  Tags = 'p.tags',
  /** 金额 */
  Amount = 'p.amount',
  /** 投资方 */
  Investors = 'p.investors',
}

export type FundingFilterType = {
  itemValue: string
  handleChangeValue: (val: string) => void
}

type FundingFilterTabValueType = string & FundingFilterTabType[keyof FundingFilterTabType]

const FundingFilter = () => {
  const { locale = 'en' } = useRouter()
  const { t } = useTranslation('fundraising')

  const { faitRateItem } = useRateChange()

  const drawerRef = useRef<any>(null)

  const { tableRef } = useStore().baseStore
  const currentParams: any = tableRef?.getParams()

  const { t: detailTrans } = useTranslation('price')

  // 金额初始化值
  const amountValue = () => {
    let currentAmountValueStr = ''
    if (currentParams?.amount_start || currentParams?.amount_start === '0') {
      currentAmountValueStr += `${currentParams?.amount_start}`
    }

    if (currentParams?.amount_end || currentParams?.amount_end === '0') {
      currentAmountValueStr += `&${currentParams?.amount_end}`
    }
    return currentAmountValueStr
  }

  const amountIntValue = amountValue()

  const [tabsValue, setTabsValue] = useState<FundingFilterTabValueType>(FundingFilterTabType.Rounds)

  // 轮次 选择 value
  const [roundsSelectValue, setRoundsSelectValue] = useState<string>(currentParams?.rounds ?? '')
  // 标签选择value
  const [tagsSelectValue, setTagsSelectValue] = useState<string>(currentParams?.tag ?? '')
  // 金额选择value
  const [amountSelectValue, setAmountSelectValue] = useState<string>(amountIntValue)
  // 投资方选择value
  const [investorsSelectValue, setInvestorsSelectValue] = useState<string>(currentParams?.investor ?? '')

  /**
   * @description: 一级tab change
   * @param {string} tab
   * @return {*}
   */
  const onTabChange = (tab: FundingFilterTabValueType): void => {
    setTabsValue(tab)
  }

  const { run: handleRoundsSelect } = useDebounceFn(
    () => {
      const selectParams = {
        ...currentParams,
        page: 1,
        pageSize: 20,
      } as any

      // 处理轮次参数
      if (roundsSelectValue?.length === 0) {
        delete selectParams?.rounds
      } else {
        selectParams.rounds = roundsSelectValue
      }

      // 处理标签参数
      if (tagsSelectValue?.length === 0) {
        delete selectParams?.tag
      } else {
        selectParams.tag = tagsSelectValue
      }

      // 处理金额参数
      if (amountSelectValue?.length === 0) {
        delete selectParams?.amount_start
        delete selectParams?.amount_end
      } else {
        console.log(
          'amountSelectValue',
          bigNumberMultipliedBy(amountSelectValue?.split('&')?.[0], 1e6),
          bigNumberMultipliedBy(amountSelectValue?.split('&')?.[1], 1e6)
        )
        const amount_start =
          Math.floor(
            Number(
              bigNumberDividedBy(bigNumberMultipliedBy(amountSelectValue?.split('&')?.[0], 1e6), faitRateItem?.rate)
            )
          ) + ''
        const amount_end =
          Math.ceil(
            Number(
              bigNumberDividedBy(bigNumberMultipliedBy(amountSelectValue?.split('&')?.[1], 1e6), faitRateItem?.rate)
            )
          ) + ''

        if (amount_start || amount_start === '0') {
          selectParams.amount_start = amount_start
        }
        if (amount_end || amount_end === '0') {
          selectParams.amount_end = amount_end
        }
      }

      // 处理投资方参数
      if (investorsSelectValue?.length === 0) {
        delete selectParams?.investor
      } else {
        selectParams.investor = investorsSelectValue
      }

      tableRef?.fetchData(selectParams, HOME_PAGE_URL.Fundraising, locale)
      drawerRef?.current?.onClose()
    },
    {
      wait: 200,
    }
  )

  const { run: handleRoundsReset } = useDebounceFn(
    () => {
      // 只重置当前 tab 的值
      if (tabsValue === FundingFilterTabType.Rounds) {
        setRoundsSelectValue('')
      } else if (tabsValue === FundingFilterTabType.Tags) {
        setTagsSelectValue('')
      } else if (tabsValue === FundingFilterTabType.Amount) {
        setAmountSelectValue('')
      } else if (tabsValue === FundingFilterTabType.Investors) {
        setInvestorsSelectValue('')
      }
    },
    {
      wait: 200,
    }
  )

  return (
    <BaseDrawer
      ref={drawerRef}
      isScroll={false}
      isButton={false}
      isRight={false}
      size={'80vh'}
      title={false}
      withCloseButton={false}
      target={
        <Button
          rightIcon={
            <svg className="iconpark-icon size-20 icon-1">
              <use href="#icon-16px-line-shaixuan"></use>
            </svg>
          }
          type="text"
        />
      }
      customClose={true}
    >
      <FundingFilterStyled data-ar={String(locale === 'ar')}>
        <Tabs type="primary" value={tabsValue} onTabChange={onTabChange} w="100%">
          <Tabs.List>
            {Object.values(FundingFilterTabType).map(key => (
              <Tabs.Tab key={key} value={key}>
                {t(`${key}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {/* 轮次 */}
          <Tabs.Panel value={FundingFilterTabType.Rounds}>
            <FFRounds
              itemValue={roundsSelectValue}
              handleChangeValue={value => {
                setRoundsSelectValue(value)
              }}
            />
          </Tabs.Panel>
          {/* 标签 */}
          <Tabs.Panel value={FundingFilterTabType.Tags}>
            <FFTags
              itemValue={tagsSelectValue}
              handleChangeValue={value => {
                setTagsSelectValue(value)
              }}
            />
          </Tabs.Panel>
          {/* 金额 */}
          <Tabs.Panel value={FundingFilterTabType.Amount}>
            <FFAmount
              itemValue={amountSelectValue}
              handleChangeValue={value => {
                setAmountSelectValue(value)
              }}
            />
          </Tabs.Panel>
          {/* 投资方 */}
          <Tabs.Panel value={FundingFilterTabType.Investors}>
            <FFInvestors
              itemValue={investorsSelectValue}
              handleChangeValue={value => {
                setInvestorsSelectValue(value)
              }}
            />
          </Tabs.Panel>
        </Tabs>
      </FundingFilterStyled>
      <Flex pl={12} pr={12}>
        <FundingFilterFooterStyled>
          <Flex justify="space-between" w="100%" gap={12}>
            <Button
              type="outlined"
              onClick={() => {
                handleRoundsReset()
              }}
              styles={{
                root: { width: '100%', height: 44 },
              }}
              label={detailTrans('P.Reset') as string}
            />
            <Button
              styles={{
                root: { width: '100%', height: 44 },
              }}
              label={t('p.confirm') as string}
              onClick={() => {
                handleRoundsSelect()
              }}
            />
          </Flex>
        </FundingFilterFooterStyled>
      </Flex>
    </BaseDrawer>
  )
}

export default observer(FundingFilter)
